import { __assign, __rest } from "tslib";
import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import clsx from 'clsx';
import InternalBox from '../box/internal';
import { InternalButton } from '../button/internal';
import { getBaseProps } from '../internal/base-component';
import { useSplitPanelContext } from '../internal/context/split-panel-context';
import useFocusVisible from '../internal/hooks/focus-visible';
import { applyDisplayName } from '../internal/utils/apply-display-name';
import ResizeHandler from './icons/resize-handler';
import PreferencesModal from './preferences-modal';
import { useMouseEvents } from './utils/use-mouse-events';
import { useKeyboardEvents } from './utils/use-keyboard-events';
import styles from './styles.css.js';
import useBaseComponent from '../internal/hooks/use-base-component';
import mergeRefs from 'react-merge-refs';
import { AppLayoutDomContext } from '../internal/context/app-layout-context';
var MIN_HEIGHT = 160;
var MIN_WIDTH = 280;
export default function SplitPanel(_a) {
    var _b, _c;
    var header = _a.header, children = _a.children, _d = _a.hidePreferencesButton, hidePreferencesButton = _d === void 0 ? false : _d, i18nStrings = _a.i18nStrings, restProps = __rest(_a, ["header", "children", "hidePreferencesButton", "i18nStrings"]);
    var __internalRootRef = useBaseComponent('SplitPanel').__internalRootRef;
    var _e = useSplitPanelContext(), size = _e.size, maxWidth = _e.maxWidth, maxHeight = _e.maxHeight, position = _e.position, topOffset = _e.topOffset, bottomOffset = _e.bottomOffset, mainPanelWidth = _e.mainPanelWidth, disableContentPaddings = _e.disableContentPaddings, isOpen = _e.isOpen, isMobile = _e.isMobile, splitPanelRef = _e.splitPanelRef, onPreferencesChange = _e.onPreferencesChange, onResize = _e.onResize, onToggle = _e.onToggle;
    var baseProps = getBaseProps(restProps);
    var focusVisible = useFocusVisible();
    var _f = useState(false), isPreferencesOpen = _f[0], setPreferencesOpen = _f[1];
    var setSidePanelWidth = useCallback(function (width) {
        var size = width;
        if (size < MIN_WIDTH) {
            size = MIN_WIDTH;
        }
        else if (maxWidth && size > maxWidth) {
            size = maxWidth;
        }
        if (isOpen && maxWidth >= MIN_WIDTH) {
            onResize({ size: size });
        }
    }, [maxWidth, isOpen, onResize]);
    var setBottomPanelHeight = useCallback(function (height) {
        var size = height;
        if (size < MIN_HEIGHT) {
            size = MIN_HEIGHT;
        }
        else if (size > maxHeight) {
            size = maxHeight;
        }
        if (isOpen && maxHeight >= MIN_HEIGHT) {
            onResize({ size: size });
        }
    }, [isOpen, maxHeight, onResize]);
    var updateSize = useCallback(function () {
        var maxSize = position === 'side' ? maxWidth : maxHeight;
        var minSize = position === 'side' ? MIN_WIDTH : MIN_HEIGHT;
        // do not resize if the panel doesn't fit
        if (maxSize < minSize) {
            return;
        }
        var newSize = size;
        if (newSize < minSize) {
            newSize = minSize;
        }
        else if (maxSize && newSize > maxSize) {
            newSize = maxSize;
        }
        if (isOpen && size !== newSize) {
            onResize({ size: newSize });
        }
    }, [isOpen, maxHeight, maxWidth, onResize, position, size]);
    // Update size when maxSize changes
    // This happens when the navigation or tools panels are toggled
    useEffect(function () {
        updateSize();
    }, [maxWidth, maxHeight, updateSize]);
    var sizeControlProps = {
        position: position,
        splitPanelRef: splitPanelRef,
        setSidePanelWidth: setSidePanelWidth,
        setBottomPanelHeight: setBottomPanelHeight
    };
    var onSliderMouseDown = useMouseEvents(sizeControlProps);
    var onKeyDown = useKeyboardEvents(sizeControlProps);
    var wrappedChildren = (React.createElement(AppLayoutDomContext.RootProvider, { value: {
            stickyOffsetTop: topOffset,
            stickyOffsetBottom: bottomOffset
        } }, children));
    var paneHeader = (React.createElement("div", { className: styles.header },
        React.createElement(InternalBox, { variant: "h2", padding: "n", margin: "n" }, header),
        React.createElement("div", { className: styles['header-actions'] },
            !hidePreferencesButton && isOpen && !isMobile && (React.createElement(React.Fragment, null,
                React.createElement(InternalButton, { className: styles['preferences-button'], iconName: "settings", variant: "inline-icon", onClick: function () { return setPreferencesOpen(true); }, formAction: "none", ariaLabel: i18nStrings.preferencesTitle }),
                React.createElement("span", { className: styles.divider }))),
            isOpen ? (React.createElement(InternalButton, { className: styles['close-button'], iconName: "close", variant: "inline-icon", onClick: onToggle, formAction: "none", ariaLabel: i18nStrings.closeButtonAriaLabel })) : (React.createElement(InternalButton, { className: styles['open-button'], iconName: "angle-up", variant: "inline-icon", formAction: "none", ariaLabel: i18nStrings.openButtonAriaLabel })))));
    /*
      This effect forces the browser to recalculate the layout
      whenever the split panel might have moved.
  
      This is needed as a workaround for a bug in Safari, which does
      not automatically calculate the new position of the split panel
      _content_ when the split panel moves.
    */
    useLayoutEffect(function () {
        var root = __internalRootRef.current;
        if (root) {
            var property = 'transform';
            var temporaryValue = 'translateZ(0)';
            var valueBefore = root.style[property];
            root.style[property] = temporaryValue;
            // This line forces the browser to recalculate the layout
            void root.offsetHeight;
            root.style[property] = valueBefore;
        }
    }, [mainPanelWidth, __internalRootRef]);
    return (React.createElement(React.Fragment, null,
        position === 'side' && (React.createElement("div", __assign({}, baseProps, { className: clsx(baseProps.className, styles.drawer, styles.root, styles['position-side'], (_b = {},
                _b[styles['drawer-closed']] = !isOpen,
                _b)), ref: mergeRefs([splitPanelRef || null, __internalRootRef]), style: { width: isOpen ? size : undefined } }),
            React.createElement("aside", { style: { top: topOffset, bottom: bottomOffset, width: isOpen ? size : undefined }, className: styles['drawer-content-side'], onClick: function () { return !isOpen && onToggle && onToggle(); } },
                isOpen ? (React.createElement("div", { className: styles['slider-wrapper-side'] },
                    React.createElement("div", __assign({ role: "slider", tabIndex: 0, "aria-label": i18nStrings.resizeHandleAriaLabel, "aria-valuemax": maxWidth, "aria-valuemin": MIN_WIDTH, "aria-valuenow": size, className: clsx(styles.slider, styles['slider-side']), onKeyDown: onKeyDown, onMouseDown: onSliderMouseDown }, focusVisible),
                        React.createElement(ResizeHandler, { className: clsx(styles['slider-icon'], styles['slider-icon-side']) })))) : (React.createElement(InternalButton, { className: clsx(styles['open-button'], styles['open-button-side']), iconName: "angle-left", variant: "icon", formAction: "none", ariaLabel: i18nStrings.openButtonAriaLabel })),
                React.createElement("div", { className: styles['content-side'], "aria-hidden": !isOpen },
                    paneHeader,
                    wrappedChildren)))),
        position === 'bottom' && (React.createElement("div", __assign({}, baseProps, { className: clsx(baseProps.className, styles.root, styles.drawer, styles['position-bottom'], (_c = {},
                _c[styles['drawer-closed']] = !isOpen,
                _c[styles['drawer-mobile']] = isMobile,
                _c[styles['drawer-disable-content-paddings']] = disableContentPaddings,
                _c)), onClick: function () { return !isOpen && onToggle(); }, style: { bottom: bottomOffset, width: mainPanelWidth, height: isOpen ? size : undefined }, ref: mergeRefs([splitPanelRef || null, __internalRootRef]) }),
            isOpen && (React.createElement("div", { className: styles['slider-wrapper-bottom'] },
                React.createElement("div", __assign({ role: "slider", tabIndex: 0, "aria-label": i18nStrings.resizeHandleAriaLabel, "aria-valuemax": maxHeight, "aria-valuemin": MIN_HEIGHT, "aria-valuenow": size, className: clsx(styles.slider, styles['slider-bottom']), onKeyDown: onKeyDown, onMouseDown: onSliderMouseDown }, focusVisible),
                    React.createElement(ResizeHandler, { className: clsx(styles['slider-icon'], styles['slider-icon-bottom']) })))),
            React.createElement("div", { className: styles['drawer-content-bottom'] },
                React.createElement("div", { className: clsx(styles['pane-header-wrapper-bottom']) }, paneHeader),
                React.createElement("div", { className: clsx(styles['content-bottom']), "aria-hidden": !isOpen }, wrappedChildren)))),
        isPreferencesOpen && (React.createElement(PreferencesModal, { visible: true, preferences: { position: position }, i18nStrings: {
                header: i18nStrings.preferencesTitle,
                confirm: i18nStrings.preferencesConfirm,
                cancel: i18nStrings.preferencesCancel,
                positionLabel: i18nStrings.preferencesPositionLabel,
                positionDescription: i18nStrings.preferencesPositionDescription,
                positionBottom: i18nStrings.preferencesPositionBottom,
                positionSide: i18nStrings.preferencesPositionSide
            }, onConfirm: function (preferences) {
                onPreferencesChange(__assign({}, preferences));
                setPreferencesOpen(false);
            }, onDismiss: function () {
                setPreferencesOpen(false);
            } }))));
}
applyDisplayName(SplitPanel, 'SplitPanel');
//# sourceMappingURL=index.js.map